import ClientRepository from '../../repositories/ClientRepository';
import Vue from 'vue';
import DeviceRepository from '../../repositories/DeviceRepository';
import DeviceTypeRepository from '../../repositories/DeviceTypeRepository';
import GroupRepository from '../../repositories/GroupRepository';
import makeDeviceAttributeSlug from '../../util/make-device-attribute-slug';

export default {
	namespaced: true,
	state: {
		currentId: null,
		all: {}
	},
	mutations: {
		current: (state, client) => {
			state.currentId = client;
		},
		SET_ALL: (state, clients) => {
			const clientSet = {};

			clients.forEach(element => {
				Vue.set(clientSet, element.id, element);
			});

			state.all = clientSet;
		},
		SET_CLIENT_NOTIFICATIONS: (state, notifications) => {
			Vue.set(state.all[state.currentId], 'notifications', notifications);
		},
		SET_CLIENT_UNASSIGNED_DEVICES: (state, devices) => {
			Vue.set(state.all[state.currentId], 'unassigned_devices', devices);
		},
		SET_CLIENT_DEVICE_TYPES: (state, types) => {
			Vue.set(state.all[state.currentId], 'device_types', types);
		},
		REMOVE_CLIENT: (state, clientId) => {
			Vue.delete(state.all, clientId);
		},
		UPDATE_CW_SETTINGS: (state, data) => {
			state.all[state.currentId].cw_plugin_enabled = data.enabled ? 1 : 0;
			state.all[state.currentId].cw_settings = data.cwData;
		},
		UPDATE_MONITOR_COUNT: (state, count) => {
			state.all[state.currentId].monitor_count = count;
		},
		/**
		 * set the list of groups in a client
		 *
		 * @param state vuex state object
		 * @param data should have 2 properties, clientId (id of client groups) & groups (list of groups for the client)
		 * @constructor
		 */
		SET_CLIENT_GROUPS: (state, data) => {
			state.all[data.clientId].groups = data.groups;
		}
	},
	actions: {
		refresh ({ commit, getters }) {
			return ClientRepository.all()
				.then(data => {
					commit('SET_ALL', data);
				});
		},
		notifications ({ commit, getters }, id) {
			return ClientRepository.getNotifications(id)
				.then(data => {
					commit('SET_CLIENT_NOTIFICATIONS', data);

					return data;
				});
		},
		getUnassignedDevices ({ commit, getters }, requestData) {
			return DeviceRepository.getUnassigned(requestData.clientId, requestData.page, requestData.sortBy, requestData.sortDirection, requestData.filter, requestData.typeId, requestData.deviceTypeAttributeIds)
				.then(data => {
					// Flatten device's attributes to the device record
					data.data = data.data.map(device => {
						/**
						 * device.device_attributes are an array of DeviceAttribute records
						 *	from the core. At time of writing, each array elements looks like:
						 *	{
						 *		id: 1,
						 *		name: 'Description',
						 *		pivot: {
						 *			value: 'This is a test device'
						 *		}
						 *	}
						 *
						 * Each element also contains all columns returned from the core,
						 *	such as created_at, etc.
						 */
						device?.device_attributes?.forEach(attr => {
							device[makeDeviceAttributeSlug(attr.name)] = attr.pivot?.value;
						});

						// We only needed to join device_attributes for the name and value.
						//	We can remove the array of attributes now since they are a
						//	large unnecessary payload
						delete device.device_attributes;

						return device;
					});

					commit('SET_CLIENT_UNASSIGNED_DEVICES', data.data);

					return data;
				});
		},
		getDeviceTypes ({ commit, getters }, options) {
			let clientId = null;
			let withAttributes = false;

			if (typeof options === 'object') {
				clientId = options.clientId;
				withAttributes = options.withAttributes;
			}
			else {
				clientId = options;
			}

			return DeviceTypeRepository.getFromClient(clientId, withAttributes)
				.then(data => {
					commit('SET_CLIENT_DEVICE_TYPES', data);

					return data;
				});
		},
		deleteClient ({ commit, getters }, clientId) {
			return ClientRepository.deleteClient(clientId)
				.then(data => {
					commit('REMOVE_CLIENT', clientId);

					return data;
				});
		},
		getGroups ({ commit, getters }, clientId) {
			return ClientRepository.getRootGroups(clientId);
		},
		fetchMagTemplates ({ commit, getters }, clientId) {
			return ClientRepository.getMagTemplates(clientId);
		},
		fetchAllGroups ({ commit, getters }, clientId) {
			return GroupRepository.all(clientId)
				.then(response => {
					const commitData = {
						clientId: clientId,
						groups: response
					};

					commit('SET_CLIENT_GROUPS', commitData);

					return response;
				});
		},

		queryGroups ({ commit, getters }, requestData) {
			return GroupRepository.queryGroups(requestData.clientId, requestData.page, requestData.filter)
				.then(response => response);
		},

		fetchSimple ({ commit, getters }) {
			return ClientRepository.roleAll();
		}
	},
	getters: {
		current: state => state.all[state.currentId] || null,
		all: state => Object.values(state.all),
		find: state => id => {
			return state.all[id] || null;
		}
	}
};
